import { useCallback } from 'react';

import type { FormInstance } from '@arco-design/web-react/es/Form';

import type { MaasModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import type { MaasModel, MaasModelSourceEntry } from './modelInstanceTypes';
import { buildModelSourceEntryFromDraft, getModelSourceType, normalizeModelSources, resolveLockedModelSourceType } from './modelSources';

/** 读写 model.sources 列表 */
export function readModelSourcesFromForm(form: FormInstance): MaasModelSourceEntry[] {
  const raw = form.getFieldValue('model.sources');
  if (Array.isArray(raw)) return raw as MaasModelSourceEntry[];
  const model = form.getFieldValue('model') as MaasModel | undefined;
  return normalizeModelSources(model);
}

export function setModelSourcesOnForm(form: FormInstance, sources: MaasModelSourceEntry[]) {
  const model = (form.getFieldValue('model') || {}) as MaasModel;
  form.setFieldsValue({
    model: { ...model, sources },
  });
}

export function clearModelSourceDraftFields(form: FormInstance) {
  form.setFieldsValue({
    'model.name': undefined,
    'model.size': undefined,
    'model.source': undefined,
  });
}

/** 清空弹窗草稿；多源场景可预置与首条一致的来源类型 */
export function resetModelSourceDraftForAdd(form: FormInstance, lockedSourceType?: MaasModelSourceType) {
  clearModelSourceDraftFields(form);
  if (!lockedSourceType) return;
  form.setFieldValue('model.source', {
    modelscope: undefined,
    huggingface: undefined,
    stackscope: undefined,
    modelplaza: undefined,
    [lockedSourceType]: {},
  });
}

export function useModelSourcesList(form: FormInstance) {
  const appendFromDraft = useCallback(() => {
    const name = String(form.getFieldValue('model.name') || '').trim();
    const size = String(form.getFieldValue('model.size') || '').trim();
    const source = form.getFieldValue('model.source') as Record<string, unknown> | undefined;
    const entry = buildModelSourceEntryFromDraft(name, size, source);
    if (!entry) return false;
    const existing = readModelSourcesFromForm(form);
    const lockedType = resolveLockedModelSourceType(existing);
    const draftType = getModelSourceType(source);
    if (lockedType && draftType && lockedType !== draftType) return false;
    const next = [...existing, entry];
    setModelSourcesOnForm(form, next);
    clearModelSourceDraftFields(form);
    return true;
  }, [form]);

  const updateEntryAt = useCallback(
    (index: number, patch: Pick<MaasModelSourceEntry, 'name' | 'size'>) => {
      const list = readModelSourcesFromForm(form);
      if (index < 0 || index >= list.length) return;
      const next = [...list];
      next[index] = { ...next[index], name: patch.name.trim(), size: patch.size.trim() };
      setModelSourcesOnForm(form, next);
    },
    [form],
  );

  const removeAt = useCallback(
    (index: number) => {
      const list = readModelSourcesFromForm(form);
      setModelSourcesOnForm(
        form,
        list.filter((_, i) => i !== index),
      );
    },
    [form],
  );

  const clearAllSources = useCallback(() => {
    setModelSourcesOnForm(form, []);
    clearModelSourceDraftFields(form);
  }, [form]);

  const replaceAll = useCallback(
    (sources: MaasModelSourceEntry[]) => {
      setModelSourcesOnForm(form, sources);
    },
    [form],
  );

  return { appendFromDraft, updateEntryAt, removeAt, clearAllSources, replaceAll, read: () => readModelSourcesFromForm(form) };
}
